import { attr, customElement, GenesisElement, observable, volatile } from '@genesislcap/web-core';
import { Auth, Connect } from '@genesislcap/foundation-comms';
import { Modal } from '@genesislcap/foundation-ui';
import { dealSubmitPreviewTemplate as template } from './deal-submit-preview.template';
import { dealSubmitPreviewStyles as styles } from './deal-submit-preview.styles';
import { BondSeries } from '../deal-detail.types';
import type { MunisDeal } from '../deal-detail.types';
import { MaturityOrder } from '../../deal-series/deal-series.types';
import {
  hasAnyEditedOrder,
  isOrderEligibleForReconfirm,
  isOrderInstructedForReconfirm,
  mapOrderForSubmitOrReconfirm,
} from '../deal-reconfirm.utils';
import { isOrderPassesSubmissionAmountRules, orderAmountFormatter } from '../../deal-series/deal-series.util';
import {
  isOrderActionableForReconfirmSubmit,
  isOrderValidForSubmission,
  isZeroCancelIntentOrder,
} from './deal-submit-preview-grid.utils';
import { computeDealActionEligibility } from '../deal-action-eligibility';
import { Permission } from '../../../../constants';
import { notifyApiError } from '../../../../utils/utils';
import { getSubmitModalMaturityOrders } from '../../deal-series/submit-preview-order.util';
import { DealSubmitPreviewGrid } from './deal-submit-preview-grid';

@customElement({
  name: 'deal-submit-preview',
  template,
  styles,
})
export class DealSubmitPreview extends GenesisElement {
  @Auth private auth: Auth;

  @attr({ mode: 'boolean' }) cancellation: boolean;

  /** Deal is in Verbal Award/CXL Window; submit shows reconfirm CTA first, grid shows reconfirm-eligible + edited. */
  @attr({ attribute: 'in-reprice-window', mode: 'boolean' }) inRepriceWindow: boolean;

  /** When true (e.g. in reprice window), cancel modal shows only affected (reconfirm-eligible) orders. */
  @attr({ attribute: 'cancel-only-affected-orders', mode: 'boolean' }) cancelOnlyAffectedOrders: boolean;

  /** Deal status (e.g. VERBAL_AWARD_CXL_WINDOW). Used for reconfirm eligibility when mapping orders for submit. */
  @attr({ attribute: 'deal-status' }) dealStatus: string | null;

  @observable munisDeal: MunisDeal | null;

  @Connect connect: Connect;

  /** When true, show reconfirm rapid-modal instead of submitting immediately (submit mode, reprice window). */
  @observable showReconfirmCta: boolean;

  @observable series: BondSeries;

  @observable submitOrdersBySeriesId: Map<string, MaturityOrder[]>;

  /** Main grid snapshot (ALL_MATURITY_ORDERS); used to restore sole-order rows in the submit modal. */
  @observable ordersBySeriesId: Map<string, MaturityOrder[]>;

  /** Bumped by deal-detail on each successful REQ_DEAL_SUBMIT_ORDERS refresh. */
  @observable submitOrdersVersion: number = 0;

  @observable bondSeriesList: BondSeries[];

  submitOrdersBySeriesIdChanged(): void {
    this.syncGridsFromSubmitOrders(true);
  }

  ordersBySeriesIdChanged(): void {
    this.syncGridsFromSubmitOrders();
  }

  submitOrdersVersionChanged(): void {
    this.syncGridsFromSubmitOrders(true);
  }

  private get allSubmitOrders(): MaturityOrder[] {
    return Array.from(this.submitOrdersBySeriesId?.values() ?? []).flat();
  }

  @observable submitInProgress: boolean;

  @observable error: boolean;

  @observable submitSuccess: boolean;

  @observable submittedOrders: MaturityOrder[];

  @observable selectedOrdersBySeriesId: Map<string, MaturityOrder[]> = new Map();

  private get allSelectedOrders(): MaturityOrder[] {
    return Array.from(this.selectedOrdersBySeriesId.values()).flat();
  }

  @observable errorText: string[];

  @observable isWarning: boolean;

  reconfirmCtaModal: Modal;

  @volatile get hasSubmitOrCancelPermissions(): boolean {
    return (
      this.auth.currentUser.hasPermission(Permission.SUBMIT_MUNI_ORDERS) ||
      this.auth.currentUser.hasPermission(Permission.CANCEL_MUNI_ORDERS) ||
      this.auth.currentUser.hasPermission(Permission.DELETE_MUNI_ORDER)
    );
  }

  /** At least one order in the deal is SUBMITTED_AND_DRAFT (edited in reprice window). */
  @volatile get hasAnyEditedOrderInDeal(): boolean {
    return hasAnyEditedOrder(this.allSubmitOrders);
  }

  private getAllDealOrders(): MaturityOrder[] {
    return this.ordersBySeriesId ? Array.from(this.ordersBySeriesId.values()).flat() : [];
  }

  @volatile get submittingAmountTotal(): number {
    return this.computeSubmitPreviewSummary().submittingAmountTotal;
  }

  @volatile get previouslySubmittedAmountTotal(): number {
    return this.computeSubmitPreviewSummary().previouslySubmittedAmountTotal;
  }

  private formatOrderAmountTotal(amount: number): string {
    return amount === 0 ? '0' : orderAmountFormatter(amount);
  }

  @volatile get submittingSummaryAmount(): string {
    return this.formatOrderAmountTotal(this.submittingAmountTotal);
  }

  @volatile get previouslySubmittedSummaryAmount(): string {
    return this.formatOrderAmountTotal(this.previouslySubmittedAmountTotal);
  }

  /** Resolves the order list that would be sent on submit/cancel. */
  private resolveOrdersForSubmitAction(): MaturityOrder[] {
    const allSelected =
      !this.cancellation && this.inRepriceWindow
        ? this.allSubmitOrders.filter((o) => !!o)
        : this.allSelectedOrders.filter((o) => !!o);

    if (this.cancellation) return allSelected;

    if (this.inRepriceWindow) {
      return allSelected.filter(
        (o) =>
          isOrderEligibleForReconfirm(o, this.dealStatus) &&
          isOrderPassesSubmissionAmountRules(o),
      );
    }

    return allSelected.filter(
      (o) =>
        isOrderValidForSubmission(o) ||
        (isOrderEligibleForReconfirm(o, this.dealStatus) &&
          isOrderPassesSubmissionAmountRules(o)),
    );
  }

  private sumOrderQuantities(orders: MaturityOrder[]): number {
    return orders.reduce((acc, o) => acc + (parseFloat(o.QUANTITY) || 0), 0);
  }

  /** Persisted submitted quantity: SUBMITTED_QUANTITY when set, else QUANTITY. */
  private resolvePreviouslySubmittedQuantity(order: MaturityOrder): number {
    if (order.SUBMITTED_QUANTITY) {
      const parsed = parseFloat(order.SUBMITTED_QUANTITY);
      if (!isNaN(parsed)) return parsed;
    }
    return parseFloat(order.QUANTITY) || 0;
  }

  private sumPreviouslySubmittedQuantities(orders: MaturityOrder[]): number {
    return orders.reduce((acc, o) => acc + this.resolvePreviouslySubmittedQuantity(o), 0);
  }

  private filterPreviouslySubmittedOrders(orders: MaturityOrder[]): MaturityOrder[] {
    return orders.filter((o) => isOrderInstructedForReconfirm(o));
  }

  private computeSubmitPreviewSummary(): {
    submittingAmountTotal: number;
    previouslySubmittedAmountTotal: number;
  } {
    if (this.cancellation) {
      return { submittingAmountTotal: 0, previouslySubmittedAmountTotal: 0 };
    }

    const dealOrders = this.allSubmitOrders;
    return {
      submittingAmountTotal: this.sumOrderQuantities(this.resolveOrdersForSubmitAction()),
      previouslySubmittedAmountTotal: this.sumPreviouslySubmittedQuantities(
        this.filterPreviouslySubmittedOrders(dealOrders),
      ),
    };
  }

  @volatile get isSubmitOrCancelEnabled(): boolean {
    if (!this.hasSubmitOrCancelPermissions) return false;

    const eligibility = computeDealActionEligibility({
      dealStatus: this.dealStatus ?? this.munisDeal?.DEAL_STATUS,
      orderPeriodBeginDate: this.munisDeal?.ORDER_PERIOD_BEGIN_DATE,
      orderPeriodEndDate: this.munisDeal?.ORDER_PERIOD_END_DATE,
      orders: this.allSubmitOrders.length > 0 ? this.allSubmitOrders : this.getAllDealOrders(),
    });

    if (this.cancellation ? !eligibility.cancelEnabled : !eligibility.submitEnabled) {
      return false;
    }

    // Reprice submit uses all actionable orders from DEAL_SUBMIT_ORDERS.
    if (!this.cancellation && this.inRepriceWindow) {
      return this.allSubmitOrders.some((o) => isOrderActionableForReconfirmSubmit(o));
    }

    // Draft submit and cancel require at least one selected row in the preview grid.
    const selected = this.allSelectedOrders;
    return selected.length > 0 && (this.cancellation || selected.some(isOrderValidForSubmission));
  }

  /** First click on Submit in reprice window: show reconfirm modal. Otherwise submit. */
  handleSubmitClick(): void {
    if (!this.cancellation && this.inRepriceWindow) {
      this.showReconfirmCta = true;
      setTimeout(() => this.reconfirmCtaModal?.show(), 0);
      return;
    }
    this.submitOrders();
  }

  /** Dismiss the reconfirm modal. */
  dismissReconfirmCta(): void {
    this.reconfirmCtaModal?.close();
    this.showReconfirmCta = false;
  }

  /** "Yes, Reconfirm All": close modal and submit. */
  async confirmReconfirmAll(): Promise<void> {
    this.reconfirmCtaModal?.close();
    this.showReconfirmCta = false;
    await this.submitOrders();
  }

  private partitionOrdersForReviewSubmitAction(orders: MaturityOrder[]): {
    cancelOrders: MaturityOrder[];
    submitOrders: MaturityOrder[];
  } {
    const cancelOrders: MaturityOrder[] = [];
    const submitOrders: MaturityOrder[] = [];
    for (const order of orders) {
      if (isZeroCancelIntentOrder(order)) {
        cancelOrders.push(order);
      } else {
        submitOrders.push(order);
      }
    }
    return { cancelOrders, submitOrders };
  }

  private async commitOrderAction(
    eventEndpoint: 'EVENT_CANCEL_MUNI_ORDERS' | 'EVENT_SUBMIT_MUNI_ORDERS',
    orders: MaturityOrder[],
  ): Promise<boolean> {
    const MUNI_ORDER_LIST = orders.map((o) => mapOrderForSubmitOrReconfirm(o, this.dealStatus));
    const response = await this.connect.commitEvent(eventEndpoint, {
      DETAILS: { MUNI_ORDER_LIST },
    });
    if (response.ERROR?.length) {
      const e = response.ERROR[0];
      this.isWarning = e?.CODE === 'VALIDATION_WARNING';
      notifyApiError(e);
      this.errorText = response.ERROR.map((x) => x.TEXT);
      this.error = true;
      return false;
    }
    if (eventEndpoint === 'EVENT_CANCEL_MUNI_ORDERS') {
      this.$emit('CANCEL_ORDER_SUCCESS', orders);
    }
    return true;
  }

  async submitOrders(): Promise<void> {
    const ordersToSend = this.resolveOrdersForSubmitAction();
    if (!this.cancellation && ordersToSend.length === 0) return;

    try {
      this.error = false;
      this.errorText = null;
      this.isWarning = false;
      this.submitInProgress = true;

      if (this.cancellation) {
        const ok = await this.commitOrderAction('EVENT_CANCEL_MUNI_ORDERS', ordersToSend);
        if (ok) {
          this.submitSuccess = true;
          this.showReconfirmCta = false;
          this.$emit('submit-success');
        }
        return;
      }

      const { cancelOrders, submitOrders } = this.partitionOrdersForReviewSubmitAction(ordersToSend);
      if (cancelOrders.length === 0 && submitOrders.length === 0) return;

      if (cancelOrders.length > 0) {
        const cancelOk = await this.commitOrderAction('EVENT_CANCEL_MUNI_ORDERS', cancelOrders);
        if (!cancelOk) return;
      }

      if (submitOrders.length > 0) {
        const submitOk = await this.commitOrderAction('EVENT_SUBMIT_MUNI_ORDERS', submitOrders);
        if (!submitOk) return;
      }

      this.submitSuccess = true;
      this.errorText = null;
      this.error = false;
      this.showReconfirmCta = false;
      this.$emit('submit-success');
    } catch (e) {
      this.error = true;
    } finally {
      this.submitInProgress = false;
    }
  }

  close(): void {
    this.resetModalState();
    this.$emit('close');
  }

  /** Clears submit result / error UI so the modal can be reopened fresh. */
  resetModalState(): void {
    this.submitSuccess = false;
    this.error = false;
    this.submitInProgress = false;
    this.showReconfirmCta = false;
    this.errorText = null;
    this.isWarning = false;
  }

  /** Re-sync grid selection into parent state when the modal opens. */
  prepareForOpen(): void {
    this.selectedOrdersBySeriesId = new Map();
    this.syncGridsFromSubmitOrders(true);
    const syncGrids = (attempt = 0): void => {
      const grids = this.getPreviewGrids();
      if (!grids.length) {
        if (attempt < 5) {
          requestAnimationFrame(() => syncGrids(attempt + 1));
        }
        return;
      }
      grids.forEach((grid) => grid.prepareForOpen());
    };
    syncGrids();
  }

  /** Imperatively push merged req-rep (+ supplemental sole) rows into each series grid. */
  syncGridsFromSubmitOrders(force = false): void {
    const syncGrids = (attempt = 0): void => {
      const grids = this.getPreviewGrids();
      if (!grids.length) {
        if (attempt < 5) {
          requestAnimationFrame(() => syncGrids(attempt + 1));
        }
        return;
      }
      grids.forEach((grid) => {
        const seriesId = grid.series?.SERIES_ID;
        if (!seriesId) return;
        const rowData = getSubmitModalMaturityOrders(
          this.ordersBySeriesId,
          this.submitOrdersBySeriesId,
          seriesId,
        );
        grid.updateRowData(rowData, force);
      });
    };
    syncGrids();
  }

  private getPreviewGrids(): DealSubmitPreviewGrid[] {
    const root = this.shadowRoot ?? this;
    return Array.from(root.querySelectorAll<DealSubmitPreviewGrid>('deal-submit-preview-grid'));
  }

  handleSelectedOrdersChanged(e: CustomEvent, seriesId: string): void {
    const next = new Map(this.selectedOrdersBySeriesId);
    next.set(seriesId, e.detail ?? []);
    this.selectedOrdersBySeriesId = next;
  }

  disconnectedCallback() {
    this.resetModalState();
    this.selectedOrdersBySeriesId = new Map();
    super.disconnectedCallback();
  }
}
